import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

export default function PerformancePage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", score: 0, max_score: 100, date: "", notes: "" });

  async function load() { const r = await api.get("/performance"); setItems(r.data.reverse()); }
  useEffect(() => { load(); }, []);

  async function create() {
    try {
      const payload = { ...form, student_id: user.id };
      await api.post("/performance", payload);
      setOpen(false); toast.success("Nota registrada"); load();
      setForm({ subject: "", score: 0, max_score: 100, date: "", notes: "" });
    } catch (e) { toast.error("Erro"); }
  }

  const chartData = items.map(p => ({ name: p.date, pct: (p.score / (p.max_score || 100)) * 100, subject: p.subject }));
  const bySubject = Object.values(items.reduce((acc, p) => {
    const key = p.subject;
    if (!acc[key]) acc[key] = { subject: key, total: 0, count: 0 };
    acc[key].total += (p.score / (p.max_score || 100)) * 100;
    acc[key].count += 1;
    return acc;
  }, {})).map(s => ({ subject: s.subject, avg: (s.total / s.count).toFixed(1) }));

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl mx-auto space-y-6">
      <header className="flex items-end justify-between fade-up flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Desempenho</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">Notas & Performance</h1>
        </div>
        {(user?.role === "student" || user?.role === "admin") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="perf-new" className="bg-[#002FA7] brutal-shadow"><Plus size={16} className="mr-1"/>Nova nota</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Registrar nota</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Matéria</Label><Input data-testid="p-subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Nota</Label><Input data-testid="p-score" type="number" value={form.score} onChange={e => setForm({...form, score: parseFloat(e.target.value) || 0})}/></div>
                  <div><Label>Máximo</Label><Input type="number" value={form.max_score} onChange={e => setForm({...form, max_score: parseFloat(e.target.value) || 100})}/></div>
                </div>
                <div><Label>Data</Label><Input data-testid="p-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}/></div>
                <div><Label>Observações</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>
                <Button data-testid="p-submit" onClick={create} className="w-full bg-[#002FA7]">Salvar</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="grid lg:grid-cols-2 gap-6 fade-up">
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Evolução</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="name" tick={{fontSize: 10, fontFamily: 'JetBrains Mono'}} />
              <YAxis domain={[0, 100]} tick={{fontSize: 10, fontFamily: 'JetBrains Mono'}} />
              <Tooltip />
              <Line type="monotone" dataKey="pct" stroke="#002FA7" strokeWidth={2} dot={{r: 3}} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <h3 className="font-heading text-lg font-bold mb-4">Média por matéria</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={bySubject}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
              <XAxis dataKey="subject" tick={{fontSize: 10}} />
              <YAxis domain={[0, 100]} tick={{fontSize: 10, fontFamily: 'JetBrains Mono'}} />
              <Tooltip />
              <Bar dataKey="avg" fill="#002FA7" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-2xl fade-up overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="py-3 px-6">Matéria</th><th>Data</th><th>Nota</th><th className="pr-6">Obs</th>
          </tr></thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-b border-zinc-100">
                <td className="py-3 px-6 font-medium">{p.subject}</td>
                <td className="font-mono-num text-zinc-500">{p.date}</td>
                <td className="font-mono-num font-bold">{p.score}/{p.max_score}</td>
                <td className="pr-6 text-zinc-500 text-xs">{p.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
